import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { api } from "@/api/apiSlice";
import { backendBase } from "@/api/backendBase";
import type { BackgroundJobDto } from "@/api/dtos";
import type { BackgroundJob } from "./_models/BackgroundJob";
import { toBackgroundJobFromBackgroundJobDto } from "./_transformers/toBackgroundJobFromBackgroundJobDto";
import { BackgroundJobTags } from "./BackgroundJobTags";

export interface CreateBackgroundJobArgs {
  kind: string;
  input: Record<string, unknown>;
}

/**
 * Starts a background job (POST /background-jobs); answers at once with the job QUEUED.
 * 409 when that kind is already running for the same subject. WRITE: whatever the job does.
 */
export const createBackgroundJobApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createBackgroundJob: builder.mutation<BackgroundJob, CreateBackgroundJobArgs>({
      queryFn: async (args) => {
        try {
          const res = await axios.post<BackgroundJobDto>(
            `${backendBase()}/background-jobs`,
            args,
            { headers: { Accept: "application/json" } },
          );
          const job = toBackgroundJobFromBackgroundJobDto(res.data);
          return { data: job };
        }
        catch (e) {
          if (axios.isAxiosError(e) && e.response) {
            return {
              error: { status: e.response.status, data: e.response.data } as FetchBaseQueryError,
            };
          }
          return {
            error: { status: "FETCH_ERROR", error: String(e) } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: [{ type: BackgroundJobTags.BackgroundJob, id: "LIST" }],
    }),
  }),
});

export const createBackgroundJob = createBackgroundJobApi.endpoints.createBackgroundJob;
